/**
 * ArTableBody Component
 *
 * Renders the table grid (CSS Grid) from the `schema` and `rows` stores,
 * subscribing to row keys plus the UI-state leaves it draws — never selection,
 * so a checkbox click cannot re-render the grid; each `ArRow` subscribes to its
 * own row. It also registers the rows surface's `formUndoStack` adapter
 * (SYD-2297) under `persist.collectionRef.path`, the key `ArCell` and
 * `useArTableActions` record against — but only while `effectiveDisabled` is
 * false, so ⌘Z can never write a table the user may not edit. That also keeps
 * PDF export, which mounts a second body over the same paths, out of the
 * registry, where its unmount would otherwise strip the interactive adapter.
 * That adapter also carries `stepOperation`, which delegates a delete's undo and
 * redo to `useArTableActions` so the pagination counters keep one writer (SYD-2447).
 *
 * Full screen (SYD-2437) is a style branch on this same container, never a
 * portal or a remount: `position: fixed` plus `overflow: auto` turns it into
 * the scrollport, so every sticky descendant retargets to it and the header's
 * offset drops the page chrome. Out of full screen that offset is a CSS `calc`
 * over `PAGE_CHROME_STICKY_TOP`, so a wrapping form header moves it without a
 * re-render. That second PDF body shares the table key, so
 * the branch is guarded by `!isPdfExportMode` too. Escape exits from this
 * container rather than from the global listener: a portaled overlay is no
 * descendant of it, so its own Escape never reaches here and cannot double-fire.
 * The one effect that only cleans up releases the claim on unmount — lifecycle
 * release of a module-global claim, not derived state kept in sync.
 *
 * Below `md` the table is in card mode: every row renders as a card over a
 * two-track grid — a flexible track and an `auto` one, so the card header puts
 * its checkbox left and its actions right while every label and value spans
 * both, stacked — and the body caps itself at
 * `--form-content-max-width` and clips sideways, so the document never grows
 * wider than a phone's viewport. Cards render only when a breakpoint below `md`
 * is positively reported — never before antd's first breakpoint dispatch, in
 * jsdom, or in PDF export. `cardMode` is computed here once and threaded down as
 * a prop; it zeroes the frozen-column count, so every left pin goes inert.
 * Outside full screen the controls strip and header are not sticky in card mode.
 */

import {
  FC,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, ConfigProvider, Grid, theme, Typography } from 'antd';
import { Timestamp } from 'firebase/firestore';
import { snapshot, useSnapshot } from 'valtio';
import get from 'lodash/get';
import set from 'lodash/set';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragCancelEvent,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import FlexCol from '../../atoms/FlexCol';
import { FORM_PAGE_CSS_VARS } from '../../atoms/FORM_PAGE_CSS_VARS';
import ArTableHeader from './ArTableHeader';
import ArRow from './ArRow';
import ArTableControls from './ArTableControls';
import { ArSelectionScopeBar } from '../selection';
import ArTableAggregations from './ArTableAggregations';
import ArTableBundleRows from './ArTableBundleRows';
import ArTableContextMenu from '../actions/ArTableContextMenu';
import { reportRowDeleteResult } from '../actions/reportRowDeleteResult';
import BulkEditConfirmationModal from './BulkEditConfirmationModal';
import BundleMoveConfirmationModal from './BundleMoveConfirmationModal';
import {
  ArTableActionsContext,
  ArTableBundleContext,
  ArTableClipboardContext,
  ArTableUIStateContext,
  ArTableViewSortContext,
} from '../context';
import {
  useArTableContinuingBundleId,
  useArTableOffPageBundleHeader,
  useArTablePaginationCursors,
  useArTableRowCount,
} from '../pagination';
import {
  formStore,
  PageScopeContext,
  useStoreCollection,
  useStoreDocument,
  useStoreNonReactive,
} from '../../../store';
import type { CollectionState, DocumentState } from '../../../store';
import { formUndoStack, type UndoTarget } from '@/store/form';
import { MessageContext } from '@/providers/MessageContext';
import {
  arTableFullScreenActions,
  arTableFullScreenState,
  PAGE_CHROME_STICKY_TOP,
} from '@/store/arTableFullScreenStore';
import { useArTableFormulas } from '../../../store/arTableFormulas';
import { useArTableAggregations } from '../../../store/arTableAggregations';
import { resolveKitRowColumnCalc } from '../../../store/arTableKitRowCalc';
import { useArTableCacheHydration } from '../useArTableCacheHydration';
import { useArTableOrderHydration } from '../useArTableOrderHydration';
import { useArTableBundleRows } from '../useArTableBundleRows';
import { admitBundleHeaders, canDragArTableRows } from '../bundleDisplay';
import type {
  ArTableRowDragState,
  BundleContinuationItem,
  BundleRenderItem,
} from '../bundleDisplay';
import { resolveArRowDrop } from '../resolveArRowDrop';
import { resolveArTableRowsOrderBy } from '../resolveArTableRowsOrderBy';
import { classifyArBundleRows } from '../classifyArBundleRows';
import { useArTableFiltering } from '../filter';
import { resolveManagedRows, useArTableDeriveProjections } from '../derives';
import { useArTableSplit } from '../split';
import { filterArRowIdsBySplit } from '../split/filterArRowIdsBySplit';
import { useColumnWidths } from '../column/useColumnWidths';
import { useFrozenLeftColumns } from '../column/useFrozenLeftColumns';
import {
  getColumnGridTrack,
  getColumnPixelWidth,
  buildColumnWidthConfigMap,
  buildPdfGridTemplate,
} from '../column/columnWidth';
import {
  isOverlayOpen,
  keybindingRegistry,
  matchChord,
  useKeyboardNavigation,
} from '@/keyboard';
import type { KeybindingId } from '@/keyboard';
import { filterVisibleColumns } from '../column/filterVisibleColumns';
import { canFullScreenTable } from '../canFullScreenTable';
import { useOrganizationParameters } from '../../../use/useOrganizationParameters';
import { applyColumnConfig } from '../column/applyColumnConfig';
import { AR_TABLE_AGGREGATIONS_ROW_ID } from '#types/System.Artifacts.types';
import type {
  ArBundleType,
  ArColumnDefinition,
  ArRowData,
  ArRowId,
  ArTableId,
  ArTableType,
  TypeId,
} from '#types/System.Artifacts.types';
import type {
  VisibilityTypeId,
  UIArTableColumnOverride,
} from '#types/System.types';
import type { ExportThemeConfig } from '@/components/pdfExport/theme/exportThemeConfig';

const EMPTY_COLLAPSED: Record<string, true> = {};

const CARD_MAX_WIDTH = `var(${FORM_PAGE_CSS_VARS.formContentMaxWidth}, 100%)`;

const CARD_GRID_TEMPLATE = 'minmax(0, 1fr) auto';

/**
 * Mirrors the rendered order onto the shared proxy, staying silent when the
 * order is unchanged. The proxy can live inside `formStore.state.tables`, so an
 * unconditional write would bump the form store's version on every table render.
 */
function syncRenderedIds<T>(target: T[], next: readonly T[]): void {
  if (
    target.length === next.length &&
    target.every((id, index) => id === next[index])
  )
    return;
  target.length = 0;
  target.push(...next);
}

/**
 * The first and last index a bundle occupies in the RENDERED list, or `null`
 * when it paints nothing. Read off the render plan rather than `bundleMap`, so
 * a bundle whose header sits on another page — and therefore owns no map entry
 * — still has an extent.
 *
 * The indices must be indices into `rowIdsForRender`, so the walk runs over the
 * ROW-BEARING items only: `bundleContinuation` is pushed into `items` with no
 * `renderedRowIds` entry (SYD-2430), and it sits at the top of the page, so
 * counting it would shift every extent by one and mis-clamp every bundle drop.
 * Across row-bearing items `items` is still pushed in lockstep with
 * `renderedRowIds`, so their indices ARE the rendered ones.
 */
function renderedBundleExtent(
  items: readonly BundleRenderItem[],
  bundleId: string,
): { first: number; last: number } | null {
  const rowItems = items.filter((item) => item.kind !== 'bundleContinuation');
  const belongs = (item: BundleRenderItem): boolean =>
    item.kind === 'bundleHeader'
      ? item.rowId === bundleId
      : item.kind === 'bundleMember' && item.bundleId === bundleId;
  const first = rowItems.findIndex(belongs);
  if (first === -1) return null;
  return { first, last: rowItems.findLastIndex(belongs) };
}

/**
 * The column an undo path writes into, or `null` when the path sits outside
 * `cells` (`annotation`, `bundleValue`) and only the row lock governs it. Paths
 * are Firestore dot-paths, so the column is always the second segment —
 * `cells.<typeId>.value.fields.<key>` included.
 */
function columnIdOfRowPath(path: string): TypeId | null {
  const segments = path.split('.');
  return segments[0] === 'cells' && segments.length > 1 ? segments[1] : null;
}

type ArTableBodyProps = {
  tableId: ArTableId;
  parentPath: string;
  disabled?: boolean;
  stickyFirstColumn?: boolean;
  visibilityMode?: VisibilityTypeId;
  /**
   * `undefined` means "every schema column, in schema order"; `[]` means "no
   * columns at all". Never collapse the former into the latter on the way down —
   * `applyColumnConfig` reduces over the list, so an empty array reaches the XLSX
   * export as zero columns and it bails with "No visible columns to export"
   * (SYD-2279). The raw prop travels to every export/import surface, and
   * `flattenColumnsForCsv` reads it the same way for the CSV template and import.
   */
  columnConfig?: UIArTableColumnOverride[];
};

/**
 * ArTableBody renders rows and columns from Data Store.
 * Uses CSS Grid for column alignment across all rows.
 */
const ArTableBody: FC<ArTableBodyProps> = ({
  tableId,
  parentPath,
  disabled = false,
  stickyFirstColumn = true,
  visibilityMode,
  columnConfig,
}) => {
  const { token } = theme.useToken();
  const configContext = useContext(ConfigProvider.ConfigContext);
  const themeConfig = configContext?.theme as ExportThemeConfig | undefined;

  const schemaStore = useStoreDocument<ArTableType>('schema');
  const schemaSnap = schemaStore.useSnapshot(schemaStore.state);
  const rowsStore = useStoreCollection<ArRowData>('rows');
  const rowsSnap = rowsStore.useSnapshot(rowsStore.state);
  const { state: schemaStateRaw } = useStoreNonReactive('schema');
  const { state: rowsStateRaw } = useStoreNonReactive('rows');
  const schemaState = schemaStateRaw as DocumentState<ArTableType>;
  const rowsStateNonReactive = rowsStateRaw as CollectionState<ArRowData>;
  const actions = useContext(ArTableActionsContext)!;
  const { messageApi } = useContext(MessageContext);
  /**
   * Ahead of `useKeyboardNavigation`, which dispatches ⌘⇧C to
   * `copyRowsToClipboard`. `ArTableProvider` owns the instance, so the body, the
   * two row menus and the bulk menu all drive the same one.
   */
  const { onCopy, onPaste, onContextMenuCapture, copyRowsToClipboard } =
    useContext(ArTableClipboardContext)!;
  const viewSortCtx = useContext(ArTableViewSortContext)!;
  const uiState = useContext(ArTableUIStateContext)!;
  const { collapsedBundles } = useSnapshot(uiState.bundles);
  const { currentPage, totalPages } = useSnapshot(uiState.pagination);
  const isPdfExportMode = Boolean(themeConfig?.pdfFormat);
  const fullScreenTableKey = formStore.tableKey(parentPath, tableId);
  const isFullScreen =
    useSnapshot(arTableFullScreenState).tableKey === fullScreenTableKey &&
    !isPdfExportMode;
  const { xs, sm, md } = Grid.useBreakpoint();
  const cardMode = !md && Boolean(xs || sm) && !isPdfExportMode;
  const pageScopeSource = useSnapshot(useContext(PageScopeContext)).source;
  const formPreviewMode = useSnapshot(formStore.state).previewMode;
  const fullScreenEligible =
    canFullScreenTable({ pageScopeSource, formPreviewMode }) &&
    !isPdfExportMode;

  /**
   * Lifecycle release of the module-global claim, the one effect here whose only
   * job is its cleanup: a claim outliving its holder would restore a scroll
   * offset belonging to a page that is gone, block the next table's capture, and
   * re-enter full screen the next time this path mounts. The PDF body shares the
   * table key and never claims, so it is excluded — its unmount must not drop
   * the interactive body's claim.
   */
  useEffect(() => {
    if (isPdfExportMode) return undefined;
    return () => arTableFullScreenActions.release(fullScreenTableKey);
  }, [isPdfExportMode, fullScreenTableKey]);

  useArTablePaginationCursors();

  const effectiveDisabled = disabled || isPdfExportMode;
  const effectiveStickyFirstColumn =
    stickyFirstColumn && !isPdfExportMode && !cardMode;
  const showInteractiveControls = !isPdfExportMode;

  const rowsPersist = rowsStore.persist;
  const rowsPath = rowsPersist.collectionRef.path;

  /**
   * The footer's "N rows". It lives here rather than in `useArTablePagination`
   * because only this side of `StoreProvider` holds the rows state whose
   * `docIds` tell it a document arrived from the server (SYD-2451).
   */
  useArTableRowCount({ uiState, rowsPath, rowsState: rowsStateNonReactive });

  /**
   * Reads and writes for `formUndoStack`. `read` runs at keystroke time and must
   * not subscribe, so it walks the live proxy rather than a snapshot.
   *
   * `apply` re-checks the same two locks `ArCell.buildUpdatesForRow` enforces —
   * `row.locked` and `cells.<typeId>.locked` — because a revert writes through
   * this adapter, not through `ArCell`, and a row can be locked after the edit
   * was recorded. A write with no surviving path issues nothing.
   *
   * `apply` beats a keystroke scheduled before the ⌘Z by ordering, not by
   * cancelling: `update(…, { debounce: false })` folds *this row's* pending
   * payload into the revert and lets the revert win the merge, leaving every
   * other row's queued write and deferred snapshot alone — which a
   * collection-wide `cancelPending()` would discard. A rejection propagates so
   * the stack never files a write that did not land onto the redo stack;
   * `useFormUndoKeybindings` owns the single user-facing report. The optimistic
   * mutation is not rolled back by hand because a rejected `updateDoc` already
   * reverts the SDK's local view, and the snapshot carrying that revert is
   * applied as soon as the row's write queue is released — so the proxy
   * converges on the server value without this adapter touching it.
   *
   * `stepDocuments` reverses a row paste (SYD-2314) and owns its own guard.
   *
   * Undo deletes by id WITHOUT checking `rowsState.docs` first. That map is one
   * page of a paginated query, so absence from it means "not on this page", not
   * "not in the collection" — undoing a paste from a later page would otherwise
   * delete nothing, report every row skipped, and still move the entry to redo,
   * where the same blind spot would rewrite the live rows and discard every edit
   * made since. A `deleteDoc` against a document that is already gone is a silent
   * no-op, so deleting unconditionally is correct from any page.
   *
   * Redo keeps its presence check, which guards a double write rather than
   * answering the same question: redo is reachable only after an undo deleted
   * these rows, so a row still on the page is one this step already restored.
   * Undo captures the row's resolved
   * `createdAt` into the entry on its way past when the row is on the page, because
   * `addRow` wrote a `serverTimestamp()` the client never held resolved at paste
   * time — and omits the field otherwise, rather than putting a second creation
   * clock in the table. The captured value is rebuilt from the
   * proxy's own numbers: Valtio proxies a `Timestamp` like any other object, and a
   * `Proxy` is what the Firestore serializer would otherwise be handed. Redo
   * writes through `set` under the entry's own `docId`, so the row returns under
   * the id the entry names, and without `bookedAnalyticsScope`, which only the
   * server writes; the trigger books the re-created row afresh. The pagination
   * counters have one writer, `useArTableActions`, so the delta is delegated rather
   * than written here.
   *
   * A partial failure bumps pagination by what landed BEFORE it rethrows, so a
   * rejection cannot leave the counters describing rows that are no longer there.
   */
  const { bumpPaginationCount, stepRowOperation } = actions;
  const rowsUndoTarget = useMemo<UndoTarget>(
    () => ({
      read: (rowId, path) =>
        get(rowsStateNonReactive.docs.get(rowId)?.data, path),
      apply: async (rowId, writes) => {
        const row = rowsStateNonReactive.docs.get(rowId);
        if (!row?.data) return;
        if (row.data.locked === true) return;

        const writable = writes.filter((write) => {
          const columnId = columnIdOfRowPath(write.path);
          if (columnId === null) return true;
          return row.data?.cells?.[columnId]?.locked !== true;
        });
        if (writable.length === 0) return;

        for (const write of writable) {
          set(row.data, write.path, write.value);
        }
        await rowsPersist.update(
          rowId,
          Object.fromEntries(
            writable.map((write) => [write.path, write.value]),
          ),
          { debounce: false },
        );
      },
      /**
       * Pure delegation, so `useArTableActions` stays the only writer of the
       * pagination counters a restore or a re-delete moves. The rejection travels
       * out untouched: `formUndoStack` puts the entry back and rethrows, and
       * `useFormUndoKeybindings` owns the single report.
       */
      stepOperation: (operationId, direction) =>
        stepRowOperation(operationId, direction),
      stepDocuments: async (docs, direction) => {
        const outcomes = await Promise.allSettled(
          docs.map(async (doc): Promise<'applied' | 'skipped'> => {
            const row = rowsStateNonReactive.docs.get(doc.docId);

            if (direction === 'undo') {
              const createdAt = row?.data?.createdAt;
              if (createdAt instanceof Timestamp) {
                doc.data.createdAt = new Timestamp(
                  createdAt.seconds,
                  createdAt.nanoseconds,
                );
              }
              await rowsPersist.delete(doc.docId, { debounce: false });
              return 'applied';
            }

            if (row?.data) return 'skipped';
            const { bookedAnalyticsScope: _bookedAnalyticsScope, ...restored } =
              doc.data;
            await rowsPersist.set(doc.docId, restored as ArRowData, {
              debounce: false,
            });
            return 'applied';
          }),
        );

        const applied = outcomes.filter(
          (outcome) =>
            outcome.status === 'fulfilled' && outcome.value === 'applied',
        ).length;
        bumpPaginationCount(direction === 'undo' ? -applied : applied);

        const rejected = outcomes.find(
          (outcome): outcome is PromiseRejectedResult =>
            outcome.status === 'rejected',
        );
        if (rejected) throw rejected.reason;

        return {
          applied,
          skipped: outcomes.length - applied,
          targetStateHolds: true,
        };
      },
    }),
    [bumpPaginationCount, rowsPersist, rowsStateNonReactive, stepRowOperation],
  );

  useEffect(() => {
    if (effectiveDisabled) return undefined;
    return formUndoStack.registerTarget(rowsPath, rowsUndoTarget);
  }, [effectiveDisabled, rowsPath, rowsUndoTarget]);

  /**
   * ⌘⌫ deletes without asking: the delete goes through the callable's trash and
   * the undo buttons carry the way back (SYD-2447). With the confirmation gone,
   * the result message is the only thing that can tell the user a row was refused,
   * so it always reports — the count, never the ids, which truncate.
   */
  const requestDeleteRows = useCallback(
    (rowIds: ArRowId[]) => {
      void actions.deleteRows({ rowIds }).then((result) => {
        reportRowDeleteResult(messageApi, result, rowIds);
      });
    },
    [actions, messageApi],
  );

  /** ⌘⇧C speaks for the focused row, the only row a keystroke names. */
  const handleCopyRows = useCallback((): void => {
    void copyRowsToClipboard('focusedRow');
  }, [copyRowsToClipboard]);

  const { onKeyDown } = useKeyboardNavigation({
    mode: 'table',
    disabled: effectiveDisabled,
    uiState,
    schemaState,
    rowsState: rowsStateNonReactive,
    actions,
    onRequestDeleteRows: requestDeleteRows,
    onCopyRows: handleCopyRows,
  });

  /**
   * Full screen is keyed from the container itself, so a portaled overlay — no
   * descendant of it — never reaches this handler. A Select-like editor does sit
   * inside, though, and keeps its trigger focused while its list is portaled, so
   * `isOverlayOpen` yields the key to it. Both chords come from the registry
   * rather than a literal, keeping `keybindings.ts` the single source of truth.
   * Entering answers to the same eligibility the toggle button does; leaving
   * never does, so a table cannot be stranded full screen.
   */
  const onContainerKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      const activeEl = document.activeElement;
      const overlayOpen =
        activeEl instanceof HTMLElement && isOverlayOpen(activeEl);
      const matches = (id: KeybindingId): boolean =>
        keybindingRegistry
          .byId(id)
          ?.chords.some((chord) => matchChord(e, chord)) ?? false;

      if (!overlayOpen) {
        if (
          (isFullScreen || fullScreenEligible) &&
          matches('table.toggleFullScreen')
        ) {
          e.preventDefault();
          arTableFullScreenActions.toggle(fullScreenTableKey);
          return;
        }
        if (isFullScreen && matches('table.exitFullScreen')) {
          arTableFullScreenActions.exit();
          return;
        }
      }
      onKeyDown(e);
    },
    [fullScreenEligible, fullScreenTableKey, isFullScreen, onKeyDown],
  );

  const { userWidths, updateWidth, resetWidth } = useColumnWidths(tableId);
  const { frozenLeftColumns, setFrozenLeftColumns } =
    useFrozenLeftColumns(tableId);

  useArTableCacheHydration();
  useArTableOrderHydration();
  useArTableFormulas();
  useArTableAggregations(parentPath, tableId);
  const deriveProjections = useArTableDeriveProjections({ tableId, uiState });

  const columns = useMemo(
    () => [...(schemaSnap.data?.columns ?? [])] as ArColumnDefinition[],
    [schemaSnap.data?.columns],
  );
  const protectedTypeIds = useMemo(
    () => new Set(schemaSnap.data?.protected?.protectedTypeIds ?? []),
    [schemaSnap.data?.protected?.protectedTypeIds],
  );
  const formulaTypeIds = useMemo(
    () => new Set(schemaSnap.data?.formulaSequence?.map((e) => e.typeId) ?? []),
    [schemaSnap.data?.formulaSequence],
  );
  const formulaSubFieldsByColumn = useMemo(() => {
    const map = new Map<TypeId, Set<string>>();
    for (const entry of schemaSnap.data?.formulaSequence ?? []) {
      const dotIndex = (entry.typeId as string).indexOf('.');
      if (dotIndex === -1) continue;
      const col = (entry.typeId as string).slice(0, dotIndex) as TypeId;
      const sub = (entry.typeId as string).slice(dotIndex + 1);
      const existing = map.get(col);
      if (existing) {
        existing.add(sub);
      } else {
        map.set(col, new Set([sub]));
      }
    }
    return map;
  }, [schemaSnap.data?.formulaSequence]);
  const formulasByTypeId = useMemo(() => {
    const map = new Map<TypeId, string>();
    for (const entry of schemaSnap.data?.formulaSequence ?? []) {
      map.set(entry.typeId, entry.formula);
    }
    return map;
  }, [schemaSnap.data?.formulaSequence]);
  const formTypeSnap = useSnapshot(formStore.state.formType);
  const kitCalcDriverTypeIds = useMemo<ReadonlySet<TypeId>>(() => {
    const resolved = resolveKitRowColumnCalc(
      schemaSnap.data?.kitRowColumnCalc,
      formTypeSnap.data?.kitRowColumnCalc,
    );
    return new Set(resolved.map((c) => c.typeId));
  }, [schemaSnap.data?.kitRowColumnCalc, formTypeSnap.data?.kitRowColumnCalc]);
  const configuredColumns = useMemo(
    () => (columnConfig ? applyColumnConfig(columns, columnConfig) : columns),
    [columns, columnConfig],
  );
  const visibleColumns = useMemo(
    () => filterVisibleColumns({ columns: configuredColumns, visibilityMode }),
    [configuredColumns, visibilityMode],
  );

  const frozenLeftCount = useMemo(() => {
    if (!effectiveStickyFirstColumn) return 0;
    if (!showInteractiveControls) return 0;
    return Math.min(frozenLeftColumns, visibleColumns.length);
  }, [
    effectiveStickyFirstColumn,
    frozenLeftColumns,
    showInteractiveControls,
    visibleColumns.length,
  ]);

  const docIds = rowsSnap.docIds;
  const viewSort = viewSortCtx.viewSort;
  const filteringSnap = useSnapshot(uiState.filtering);
  const visibleRowIds = useMemo(() => {
    return docIds.filter(
      (rowId) => rowId !== AR_TABLE_AGGREGATIONS_ROW_ID,
    ) as ArRowId[];
  }, [docIds]);

  const split = useArTableSplit(columns);

  /**
   * Filtering/Bundle subscription boundary:
   * Access rowsSnap.docs when filtering, split, or bundles need it — the grid
   * must not re-render on a write to a row nobody is grouping or narrowing.
   * The bundle path reads row data to detect bundleRole/bundleId and to count
   * each bundle's members; header admission reads it only while something is
   * narrowing, since with nothing narrowed there is no header to re-admit.
   */
  const hasActiveFilters = useMemo(() => {
    return Object.values(filteringSnap.byColumnId).some(
      (v) => v && v.length > 0,
    );
  }, [filteringSnap.byColumnId]);
  /** "A split or a column filter is narrowing this table" — see the `drag` memo. */
  const rowsWithheld = hasActiveFilters || split !== null;
  const groupsEnabled = schemaSnap.data?.bundles?.groups === true;
  const kitsEnabled = schemaSnap.data?.bundles?.kits?.enabled === true;
  const bundlesEnabled = groupsEnabled || kitsEnabled;
  const { getVisibilityTypesForFormType } = useOrganizationParameters();
  const activeVisibilityType = useMemo(() => {
    if (!visibilityMode || visibilityMode === 'internal') return undefined;
    return getVisibilityTypesForFormType(formTypeSnap.data?.docId).find(
      (v) => v.id === visibilityMode,
    );
  }, [getVisibilityTypesForFormType, formTypeSnap.data?.docId, visibilityMode]);
  const hideKitMembers =
    kitsEnabled && activeVisibilityType?.hideKitComponents === true;
  const hideGroupMembers =
    groupsEnabled && activeVisibilityType?.hideGroupComponents === true;
  /**
   * Mirrors the first two guards of `isBundleMemberCellSuppressed`: a member cell
   * can only be blanked on a non-internal view whose column carries a hide-list.
   * Rows keep their persisted `bundleRole` after bundles are disabled on the table
   * type, so without this the classification stops and those cells render in full.
   */
  const memberHideListsConfigured = useMemo(() => {
    if (!visibilityMode || visibilityMode === 'internal') return false;
    return visibleColumns.some(
      (col) =>
        (col.kitMemberHiddenTypes?.length ?? 0) > 0 ||
        (col.groupMemberHiddenTypes?.length ?? 0) > 0,
    );
  }, [visibilityMode, visibleColumns]);
  const rowsDocsForBundles = bundlesEnabled ? rowsSnap.docs : null;
  const rowsDocsForFlatMembers =
    !bundlesEnabled && memberHideListsConfigured ? rowsSnap.docs : null;
  const rowsDocsForFiltering = hasActiveFilters ? rowsSnap.docs : null;
  const rowsDocsForSplitFiltering = split ? rowsSnap.docs : null;
  /**
   * Read through `snapshot()`, not `useSnapshot`: the grid must not re-render
   * when a row checkbox is ticked. PDF mode renders once into an offscreen tree
   * whose selection is already fixed by the trigger, and interactive mode never
   * reads the value at all.
   */
  const pdfSelectedRowIds = isPdfExportMode
    ? snapshot(uiState.selection).selectedRowIds
    : null;
  const rowIdsAfterSplit = useMemo(() => {
    if (!split) return visibleRowIds;
    return filterArRowIdsBySplit({
      rowIds: visibleRowIds,
      rowsDocs: rowsDocsForSplitFiltering,
      split,
    });
  }, [rowsDocsForSplitFiltering, split, visibleRowIds]);

  const filteringResult = useArTableFiltering({
    visibleRowIds: rowIdsAfterSplit,
    filterState: filteringSnap,
    rowsDocs: rowsDocsForFiltering,
  });

  const baseFilteredRowIds = hasActiveFilters
    ? filteringResult.filteredRowIds
    : rowIdsAfterSplit;
  /**
   * "A header follows its members": a bundle keeping at least one member through
   * the split and the column filters re-admits its header, so no rendered member
   * is ever headerless. It runs on the narrowing pass only — `rowsDocs` is `null`
   * otherwise, which returns the input untouched and keeps the grid off the docs
   * subscription — and deliberately BEFORE the PDF-selection narrowing below: an
   * unticked header must keep orphaning its member so the export fails closed
   * (SYD-2279). Gated on `bundlesEnabled` alongside the narrowing: rows keep
   * their persisted `bundleRole` after bundles are switched off on the table
   * type, and re-admitting a header there would put a row the filter excluded
   * back on a flat grid that draws no bundle at all. The result is copied into a
   * mutable array because dnd-kit's `SortableContext` and `arrayMove` both
   * refuse a readonly one.
   */
  const rowsDocsForHeaderAdmission =
    bundlesEnabled && rowsWithheld ? rowsSnap.docs : null;
  const admittedRowIds = useMemo(
    () => [
      ...admitBundleHeaders({
        rowIds: visibleRowIds,
        admittedRowIds: baseFilteredRowIds,
        rowsDocs: rowsDocsForHeaderAdmission,
      }),
    ],
    [baseFilteredRowIds, rowsDocsForHeaderAdmission, visibleRowIds],
  );
  /**
   * PDF mode reads `selectedRowIds` as the explicit subset to print.
   * - empty selection ⇒ "no subset specified, print everything visible"
   * - non-empty selection ⇒ "print only these rows"
   *
   * Pagination is disabled in PDF mode (`ArTableShell` drops the page-size
   * limit), so `baseFilteredRowIds` covers every row in the table — selections
   * made on any page are honoured here. If the trigger UI ever wants
   * "selected only", it must pass a non-empty `selectedRowIds`.
   */
  const filteredRowIds = useMemo(() => {
    if (
      !isPdfExportMode ||
      !pdfSelectedRowIds ||
      pdfSelectedRowIds.length === 0
    ) {
      return admittedRowIds;
    }
    const selectedSet = new Set(pdfSelectedRowIds);
    return admittedRowIds.filter((id) => selectedSet.has(id));
  }, [admittedRowIds, isPdfExportMode, pdfSelectedRowIds]);

  /**
   * Member identity for the flat path. Rows keep their persisted `bundleRole`
   * after bundles are disabled on the table type, so their cells are still
   * subject to a column's hide-list — but the bundle render path is not: it
   * would blank header cells and expose bundle actions the feature no longer
   * maintains. Only the per-cell identity crosses over, keyed by rowId so a
   * drag reorder cannot invalidate it.
   */
  const flatMemberBundleTypes = useMemo(() => {
    if (!rowsDocsForFlatMembers) return null;
    const classification = classifyArBundleRows({
      rowIds: filteredRowIds,
      rowsDocs: rowsDocsForFlatMembers,
    });
    const byRowId = new Map<ArRowId, ArBundleType | undefined>();
    for (const row of classification.items) {
      if (row.kind === 'bundleMember') byRowId.set(row.rowId, row.bundleType);
    }
    return byRowId;
  }, [filteredRowIds, rowsDocsForFlatMembers]);

  /**
   * Every member on the PAGE, per bundle — not per narrowed render plan. The
   * badge counts the members this page paints, so the split- or filter-narrowed
   * list under-reports it. `deleteBundle` and `dissolveBundle` quote no count at
   * all: they send a `{ bundleId }` scope and the server acts on every member the
   * collection holds, on this page or the next (SYD-2430). Derived from the docs
   * map the bundle path already reads, so it adds no subscription.
   *
   * PDF mode is deliberately excluded. `filteredRowIds` is narrowed to the ticked
   * selection there, so quoting the whole-page census would print "Kit · 2 of 9"
   * and advertise the seven rows the user excluded from the export. With no entry
   * for a bundle,
   * `resolveArBundleDisplay` falls back to the painted count (SYD-2406).
   */
  const rowsDocsForMemberCensus = isPdfExportMode ? null : rowsDocsForBundles;
  const memberCountByBundleId = useMemo(() => {
    const counts = new Map<string, number>();
    if (!rowsDocsForMemberCensus) return counts;
    for (const entry of rowsDocsForMemberCensus.values()) {
      const bundleId =
        entry.data?.bundleRole === 'member' ? entry.data.bundleId : undefined;
      if (!bundleId) continue;
      counts.set(bundleId, (counts.get(bundleId) ?? 0) + 1);
    }
    return counts;
  }, [rowsDocsForMemberCensus]);

  /**
   * The table's drag posture, resolved once and shared: `canDragArTableRows`
   * turns it into the flat path's gate, and `resolveArBundleDisplay` into every
   * bundle's `dropAvailability`. `rowsWithheld` closes dragging because a rank
   * bisected between two RENDERED neighbours lands arbitrarily once the
   * narrowing that hid the rows between them clears.
   */
  const drag = useMemo<ArTableRowDragState>(
    () => ({
      manualOrderMode: viewSort.mode === 'order',
      disabled: effectiveDisabled,
      rowsWithheld,
    }),
    [effectiveDisabled, rowsWithheld, viewSort.mode],
  );

  /**
   * The two facts the page window hides, both keyed off the page's own first and
   * last rows (SYD-2430). `pageRowIds` is `visibleRowIds` because that IS the
   * page in query order, not a narrowing of it; what keeps the PDF export
   * failing closed is the window test `rowsDocs.has(bundleId)` in the hook and
   * in `resolveArBundleDisplay` — a header can be present in the window and
   * still unticked for the export (SYD-2279). `rowsOrderBy` is derived from the
   * same `viewSort` through the same pure function `ArTableShell` feeds the rows
   * store, so the probe's ordering and the page query's cannot diverge.
   */
  const rowsOrderBy = useMemo(
    () => resolveArTableRowsOrderBy(viewSort),
    [viewSort],
  );
  const offPageHeaders = useArTableOffPageBundleHeader({
    rowsPath,
    pageRowIds: visibleRowIds,
    rowsDocs: rowsDocsForBundles,
  });
  const continuingBundleId = useArTableContinuingBundleId({
    rowsPath,
    rowsOrderBy,
    pageRowIds: visibleRowIds,
    rowsDocs: rowsDocsForBundles,
    enabled: !isPdfExportMode && currentPage < totalPages && !rowsSnap.loading,
  });

  const bundleResult = useArTableBundleRows({
    visibleRowIds: filteredRowIds,
    collapsedBundles: isPdfExportMode ? EMPTY_COLLAPSED : collapsedBundles,
    rowsDocs: rowsDocsForBundles,
    hideKitMembers,
    hideGroupMembers,
    drag,
    memberCountByBundleId,
    offPageHeaders,
    continuingBundleId,
  });

  const bundleContextValue = useMemo(
    () => (bundleResult ? { bundleMap: bundleResult.bundleMap } : null),
    [bundleResult],
  );

  const dndEnabled = canDragArTableRows(drag);
  const flatReorderEnabled = dndEnabled && !bundleResult;

  const columnPixelWidths = useMemo(() => {
    return visibleColumns.map((col) =>
      getColumnPixelWidth(col, userWidths[col.typeId]),
    );
  }, [userWidths, visibleColumns]);

  // Grid: checkbox (40px) + visible data columns (minmax tracks) + actions (40px)
  const gridTemplateColumns = useMemo(() => {
    if (isPdfExportMode) {
      return buildPdfGridTemplate(visibleColumns);
    }
    if (cardMode) return CARD_GRID_TEMPLATE;
    const dataTracks = visibleColumns
      .map((col) => getColumnGridTrack(col, userWidths[col.typeId]))
      .join(' ');
    return `40px ${dataTracks} minmax(0, 1fr) 40px`;
  }, [cardMode, isPdfExportMode, userWidths, visibleColumns]);

  const frozenLeftOffsetsByIndex = useMemo(() => {
    if (!showInteractiveControls) return [];
    if (frozenLeftCount === 0) return [];

    const offsets: number[] = [];
    let runningLeft = 40;

    for (let i = 0; i < frozenLeftCount; i++) {
      offsets[i] = runningLeft;
      runningLeft += columnPixelWidths[i] ?? 0;
    }

    return offsets;
  }, [columnPixelWidths, frozenLeftCount, showInteractiveControls]);

  const columnWidthConfig = useMemo(
    () => buildColumnWidthConfigMap(visibleColumns),
    [visibleColumns],
  );
  const controlsStickyHeight =
    (token.controlHeightSM ?? token.controlHeight) + token.paddingSM * 2;

  const [dragRowIds, setDragRowIds] = useState<ArRowId[] | null>(null);
  const dragResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pendingBundleMove, setPendingBundleMove] = useState<{
    rowId: ArRowId;
    targetBundleId: string;
    /**
     * `null` when the target bundle's header is off the loaded page AND no
     * `offPageHeaders` entry named it, so nothing on screen knows the bundle's
     * name — the modal asks without one rather than quoting a raw document id at
     * the user. With an entry, the continuation item that opens the run carries
     * the name and the modal quotes it (SYD-2430).
     */
    targetBundleLabel: string | null;
    afterRowId: ArRowId | null;
  } | null>(null);

  const clearDragResetTimer = useCallback((): void => {
    const existing = dragResetTimerRef.current;
    if (!existing) return;
    clearTimeout(existing);
    dragResetTimerRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      clearDragResetTimer();
    };
  }, [clearDragResetTimer]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  /**
   * Hoisted above the loading return so the memo below it can be a hook. A flat
   * drag renders `dragRowIds` instead, which the guards keep a permutation of the
   * same page — so the set of ids never differs, only their order.
   */
  const rowIdsForRender = useMemo(() => {
    const baseRowIds = bundleResult
      ? bundleResult.renderedRowIds
      : filteredRowIds;

    if (!flatReorderEnabled) return baseRowIds;
    if (dragRowIds === null) return baseRowIds;
    if (dragRowIds.length !== baseRowIds.length) return baseRowIds;
    const visibleSet = new Set(baseRowIds);
    for (const id of dragRowIds) {
      if (!visibleSet.has(id)) return baseRowIds;
    }
    const isSynced = dragRowIds.every((id, idx) => id === baseRowIds[idx]);
    if (isSynced) return baseRowIds;
    return dragRowIds;
  }, [bundleResult, filteredRowIds, flatReorderEnabled, dragRowIds]);

  /**
   * The columns whose header carries the derived mark, for the rows on THIS page.
   * Memoized because `resolveManagedRows` mints a fresh `Set` as soon as one managed
   * row is on the page, and an unmemoized one defeats `ArTableHeader`'s own memo —
   * every keystroke would re-render the header of a projection-target table. The
   * `NONE` fast path is already frozen, so a table nothing projects into is unaffected
   * either way.
   */
  const { derivedTypeIds } = useMemo(
    () => resolveManagedRows(deriveProjections, rowIdsForRender),
    [deriveProjections, rowIdsForRender],
  );

  const showEmptyFilterMessage = rowsWithheld && filteredRowIds.length === 0;
  const showEmptyState =
    !hasActiveFilters && split === null && filteredRowIds.length === 0;
  const isPaginatedSplitEmpty =
    split !== null &&
    !hasActiveFilters &&
    filteredRowIds.length === 0 &&
    totalPages > 1;

  if (!schemaSnap.data || rowsSnap.loading) {
    return (
      <FlexCol noGap>
        <Typography.Text aria-label="table-title">
          {schemaSnap.data?.description?.shortLabel ? tableId : 'Table'}
        </Typography.Text>
        <div
          style={{
            border: `1px solid ${token.colorTextQuaternary}`,
            borderRadius: token.borderRadius,
            backgroundColor: token.colorBgContainer,
            padding: token.paddingLG,
            width: '100%',
          }}
        >
          <Typography.Text type="secondary">Loading...</Typography.Text>
        </div>
      </FlexCol>
    );
  }

  const handleDragStart = (_event: DragStartEvent) => {
    if (!dndEnabled) return;
    clearDragResetTimer();
    if (flatReorderEnabled) {
      setDragRowIds(rowIdsForRender);
    }
  };

  const handleDragCancel = (_event: DragCancelEvent) => {
    if (!dndEnabled) return;
    clearDragResetTimer();
    setDragRowIds(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!dndEnabled) return;
    clearDragResetTimer();
    const { active, over } = event;
    if (!over) {
      setDragRowIds(null);
      return;
    }

    const activeId = String(active.id) as ArRowId;
    const overId = String(over.id) as ArRowId;
    if (activeId === overId) {
      setDragRowIds(null);
      return;
    }

    const oldIndex = rowIdsForRender.indexOf(activeId);
    const newIndex = rowIdsForRender.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1) {
      setDragRowIds(null);
      return;
    }

    if (bundleResult) {
      const drop = resolveArRowDrop({
        items: bundleResult.items,
        activeRowId: activeId,
        overRowId: overId,
      });
      const nextRowIds = arrayMove(rowIdsForRender, oldIndex, newIndex);

      const activeRowLocked =
        rowsStateNonReactive.docs.get(activeId)?.data?.locked === true;

      switch (drop.kind) {
        case 'joinBundle': {
          if (activeRowLocked) return;

          /**
           * The aim anchor: the rendered row the dropped row will follow. The
           * aimed slot is the rendered predecessor whenever it belongs to the
           * target bundle, and holds whatever posture the bundle is in. Which
           * fallback that aim gets is the criteria's call, not this handler's —
           * `open` falls back to the header itself, the first-member slot, while
           * `appendOnly` (no painted members) and `closed` have nothing to aim at
           * and append. A bundle absent from the map is a member whose header
           * sits on another page: the aim still ranks the drop, but that header
           * can anchor nothing here, so it has no fallback of its own.
           */
          const afterRowId = ((): ArRowId | null => {
            const predecessorId = nextRowIds[newIndex - 1];
            const aimedRowId =
              predecessorId !== undefined &&
              rowsStateNonReactive.docs.get(predecessorId)?.data?.bundleId ===
                drop.bundleId
                ? predecessorId
                : null;

            const availability = bundleResult.bundleMap.get(
              drop.bundleId,
            )?.dropAvailability;
            if (availability === undefined) return aimedRowId;
            switch (availability.kind) {
              case 'open':
                return aimedRowId ?? drop.bundleId;
              case 'appendOnly':
                return null;
              case 'closed':
                return null;
              default: {
                const unhandled: never = availability;
                console.error(
                  '[ArTableBody] unhandled bundle drop availability',
                  unhandled,
                );
                return null;
              }
            }
          })();

          /**
           * A bundle with no map entry has its header on another page, and the
           * continuation item that opens its run carries the name read off that
           * header — so the confirmation can name the bundle instead of falling
           * through to `null` (SYD-2430).
           */
          const continuationItem = bundleResult.items.find(
            (item): item is BundleContinuationItem =>
              item.kind === 'bundleContinuation' &&
              item.bundleId === drop.bundleId,
          );

          setPendingBundleMove({
            rowId: activeId,
            targetBundleId: drop.bundleId,
            targetBundleLabel:
              bundleResult.bundleMap.get(drop.bundleId)?.label ??
              continuationItem?.label ??
              null,
            afterRowId,
          });
          return;
        }

        case 'leaveBundle': {
          if (activeRowLocked) return;
          actions.removeFromBundle(activeId);
          actions.moveRowInManualOrder(nextRowIds, activeId);
          return;
        }

        case 'moveBundle': {
          if (activeRowLocked) return;

          /**
           * A bundle is a contiguous block, so a dragged header that landed
           * inside ANOTHER bundle is clamped to that bundle's nearest rendered
           * boundary before the move — ranking it between its two rendered
           * neighbours would otherwise draw the target bundle split in half.
           *
           * `arrayMove` removes at `from` before inserting at `to`, so `to` is an
           * index into the array MINUS the dragged row: a boundary shifts down by
           * one exactly when the dragged row sat above THAT boundary. Each limb
           * therefore tests its own — `oldIndex < first` for landing before the
           * target's first rendered row, `oldIndex < last` for landing after its
           * last. The two agree while the target's rows are contiguous (the
           * header then sits wholly outside `[first, last]`), and only part
           * company when a column sort interleaves another bundle through it,
           * which `classifyArBundleRows` allows. `over` is always a row of the
           * target bundle, so `newIndex === first` means "dropped on its first
           * rendered row" — that lands above, everything else below.
           *
           * `moveBundleInManualOrder` derives its members' ceiling from the
           * first non-member following the header in this array, so clamping
           * the array is the whole fix.
           */
          const movedRowIds = ((): ArRowId[] => {
            if (drop.intoBundleId === null) return nextRowIds;
            const extent = renderedBundleExtent(
              bundleResult.items,
              drop.intoBundleId,
            );
            if (!extent) return nextRowIds;
            const clampedIndex =
              newIndex <= extent.first
                ? extent.first - (oldIndex < extent.first ? 1 : 0)
                : extent.last + 1 - (oldIndex < extent.last ? 1 : 0);
            return arrayMove(rowIdsForRender, oldIndex, clampedIndex);
          })();

          if (!bundleResult.bundleMap.has(drop.bundleId)) {
            actions.moveRowInManualOrder(movedRowIds, activeId);
            return;
          }
          actions.moveBundleInManualOrder(movedRowIds, activeId);
          return;
        }

        case 'reorder': {
          actions.moveRowInManualOrder(nextRowIds, activeId);
          return;
        }

        default: {
          const unhandled: never = drop;
          console.error('[ArTableBody] unhandled drop target', unhandled);
          return;
        }
      }
    }

    const nextRowIds = arrayMove(rowIdsForRender, oldIndex, newIndex);
    setDragRowIds(nextRowIds);
    actions.moveRowInManualOrder(nextRowIds, activeId);
    dragResetTimerRef.current = setTimeout(() => {
      setDragRowIds(null);
      dragResetTimerRef.current = null;
    }, 2_000);
  };

  /**
   * A confirmed drop joins the target bundle and does nothing else to the order:
   * `moveToBundle` ranks the row via `allocateMoveToBundleOrder`, which only
   * bisects intervals whose bounds are adjacent in the CURRENT PAGE of
   * `rowsState.docIds` — one cursor window, not the collection — and therefore
   * provably empty; past a non-final page tail it bounds by the anchor's on-page
   * predecessor, or skips the rank write (SYD-2323). The `afterRowId` captured at
   * drop time keeps the aimed position inside an expanded bundle; a collapsed or
   * hidden-member bundle appends. Following the join with `moveRowInManualOrder`
   * would instead re-rank from the RENDERED list, whose neighbours span every
   * collapsed or hidden member — bisecting that span can reproduce a rank an
   * existing member already holds (SYD-2186).
   */
  const confirmBundleMove = (): void => {
    if (pendingBundleMove) {
      actions.moveToBundle(
        pendingBundleMove.rowId,
        pendingBundleMove.targetBundleId,
        pendingBundleMove.afterRowId ?? undefined,
      );
    }
    setPendingBundleMove(null);
  };

  /**
   * What select-all governs: every rendered row plus the unpainted members
   * behind every rendered bundle header, so ticking a collapsed header cannot
   * strand its members in a delete. An expanded bundle's members are already in
   * `items`, hence the `Set`; a `bundleMember` whose header sits outside this
   * row set is emitted on its own turn, while the `bundleContinuation` that
   * opens such a run stands for no document and is never selectable.
   *
   * A header with `withheldMemberCount > 0` yields NEITHER itself nor its
   * members. Collapse and the visibility view are viewport affordances over a
   * bundle the user acts on as a unit, so their unpainted members ride along; a
   * split or column filter means the user narrowed to specific rows, and taking
   * a partially-withheld header would delete it out from under the members the
   * narrowing hid. Such a header is context for its surviving members, which
   * `items` still carries on their own turn — unless collapse hides them too,
   * in which case the whole bundle stays out, the conservative reading.
   */
  const selectableRowIds = ((): ArRowId[] => {
    if (!bundleResult) return rowIdsForRender;
    const emitted = new Set<ArRowId>();
    const ids: ArRowId[] = [];
    const emit = (rowId: ArRowId): void => {
      if (emitted.has(rowId)) return;
      emitted.add(rowId);
      ids.push(rowId);
    };
    for (const item of bundleResult.items) {
      if (item.kind === 'bundleContinuation') continue;
      if (item.kind !== 'bundleHeader') {
        emit(item.rowId);
        continue;
      }
      const bundle = bundleResult.bundleMap.get(item.rowId);
      if (bundle && bundle.withheldMemberCount > 0) continue;
      emit(item.rowId);
      for (const memberRowId of bundle?.memberRowIds ?? []) {
        emit(memberRowId);
      }
    }
    return ids;
  })();

  syncRenderedIds(
    uiState.renderedRowIds,
    bundleResult ? bundleResult.renderedRowIds : rowIdsForRender,
  );
  /**
   * Inside a form the PDF portal mounts a second body over the same
   * `formStore.registerTable` slot with the page limit dropped, so letting it
   * write this leaf would leave select-all pointing at the whole table. It has
   * no reason to: `ArTableHeader` does not render the header checkbox in PDF mode.
   */
  if (!isPdfExportMode)
    syncRenderedIds(uiState.selectableRowIds, selectableRowIds);
  syncRenderedIds(
    uiState.renderedColIds,
    visibleColumns.map((c) => c.typeId),
  );

  /**
   * Row rendering: clean bifurcation between flat and bundle modes.
   * Bundle rendering is fully delegated to ArTableBundleRows component.
   */
  const flatRows = !bundleResult
    ? rowIdsForRender.map((rowId, rowIndex) => (
        <ArRow
          key={rowId}
          rowId={rowId}
          rowIndex={rowIndex}
          disabled={effectiveDisabled}
          stickyFirstColumn={effectiveStickyFirstColumn}
          frozenLeftCount={frozenLeftCount}
          frozenLeftOffsetsByIndex={frozenLeftOffsetsByIndex}
          manualReorderEnabled={flatReorderEnabled}
          columns={visibleColumns}
          protectedTypeIds={protectedTypeIds}
          formulaTypeIds={formulaTypeIds}
          formulaSubFieldsByColumn={formulaSubFieldsByColumn}
          bundleRole={flatMemberBundleTypes?.has(rowId) ? 'member' : undefined}
          bundleType={flatMemberBundleTypes?.get(rowId)}
          cardMode={cardMode}
        />
      ))
    : null;

  const headerStickyTop = isFullScreen
    ? controlsStickyHeight
    : `calc(${PAGE_CHROME_STICKY_TOP} + ${controlsStickyHeight}px)`;

  return (
    <ArTableContextMenu
      tableId={tableId}
      parentPath={parentPath}
      disabled={effectiveDisabled}
      columnConfig={columnConfig}
    >
      <FlexCol noGap>
        <Typography.Text aria-label="table-title">
          {schemaSnap.data?.description?.shortLabel ?? tableId}
        </Typography.Text>
        <div
          onKeyDown={onContainerKeyDown}
          onCopy={onCopy}
          onPaste={onPaste}
          onContextMenuCapture={onContextMenuCapture}
          aria-label="artable-body"
          style={{
            width: isPdfExportMode
              ? 'min-content'
              : isFullScreen
                ? 'auto'
                : cardMode
                  ? '100%'
                  : 'max-content',
            minWidth: '100%',
            boxSizing: 'border-box',
            border: `1px solid ${token.colorTextQuaternary}`,
            borderRadius: isFullScreen ? 0 : token.borderRadius,
            backgroundColor: token.colorBgContainer,
            maxWidth: cardMode && !isFullScreen ? CARD_MAX_WIDTH : undefined,
            overflowX: isPdfExportMode
              ? 'visible'
              : isFullScreen
                ? 'auto'
                : 'clip',
            overflowY: isPdfExportMode
              ? 'visible'
              : isFullScreen
                ? 'auto'
                : 'clip',
            ...(isFullScreen
              ? {
                  position: 'fixed',
                  inset: 0,
                  zIndex: token.zIndexPopupBase,
                  overscrollBehavior: 'contain',
                }
              : null),
          }}
        >
          {showInteractiveControls && (
            <ArTableControls
              tableId={tableId}
              parentPath={parentPath}
              disabled={effectiveDisabled}
              stickyTop={!cardMode || isFullScreen}
              frozenLeftColumns={frozenLeftColumns}
              onFrozenLeftColumnsChange={setFrozenLeftColumns}
              columnConfig={columnConfig}
              cardMode={cardMode}
            />
          )}
          <div
            aria-label="artable-container"
            role="table"
            style={{
              display: 'grid',
              gridTemplateColumns,
              width: isPdfExportMode
                ? 'min-content'
                : cardMode
                  ? '100%'
                  : 'max-content',
              minWidth: '100%',
              boxSizing: 'border-box',
              ...(cardMode
                ? { rowGap: token.marginXS, padding: token.paddingXS }
                : null),
            }}
          >
            <ArTableHeader
              stickyFirstColumn={effectiveStickyFirstColumn}
              frozenLeftCount={frozenLeftCount}
              frozenLeftOffsetsByIndex={frozenLeftOffsetsByIndex}
              stickyTop={
                showInteractiveControls && !cardMode
                  ? headerStickyTop
                  : undefined
              }
              tableId={tableId}
              parentPath={parentPath}
              disabled={effectiveDisabled}
              onColumnResize={updateWidth}
              onColumnReset={resetWidth}
              columnWidthConfig={columnWidthConfig}
              columns={visibleColumns}
              protectedTypeIds={protectedTypeIds}
              formulasByTypeId={formulasByTypeId}
              derivedTypeIds={derivedTypeIds}
              cardMode={cardMode}
            />
            {showInteractiveControls && <ArSelectionScopeBar />}
            {showEmptyState ? (
              <div
                aria-label="artable-empty-state"
                style={{
                  gridColumn: '1 / -1',
                  padding: token.paddingSM,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 0,
                  backgroundColor: token.colorBgContainer,
                }}
              >
                <Typography.Text
                  strong
                  style={{ fontSize: token.fontSizeHeading5 }}
                >
                  No rows yet
                </Typography.Text>
                <Typography.Text type="secondary">
                  Use + Add Row above to create the first entry.
                </Typography.Text>
              </div>
            ) : showEmptyFilterMessage ? (
              <div style={{ gridColumn: '1 / -1', padding: token.paddingLG }}>
                {isPaginatedSplitEmpty ? (
                  <Typography.Text type="secondary">
                    No split rows on this page
                    <Button
                      size="small"
                      type="link"
                      aria-label="split-previous-page"
                      disabled={currentPage === 1}
                      onClick={() => {
                        uiState.pagination.currentPage = currentPage - 1;
                      }}
                    >
                      Previous page
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      aria-label="split-next-page"
                      disabled={currentPage === totalPages}
                      onClick={() => {
                        uiState.pagination.currentPage = currentPage + 1;
                      }}
                    >
                      Next page
                    </Button>
                  </Typography.Text>
                ) : (
                  <Typography.Text type="secondary">
                    {hasActiveFilters
                      ? 'No rows match the current filters'
                      : 'No rows match the current split'}
                  </Typography.Text>
                )}
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[
                  restrictToVerticalAxis,
                  restrictToFirstScrollableAncestor,
                ]}
                onDragStart={handleDragStart}
                onDragCancel={handleDragCancel}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={rowIdsForRender}
                  strategy={verticalListSortingStrategy}
                >
                  {bundleResult ? (
                    <ArTableBundleContext.Provider value={bundleContextValue}>
                      <ArTableBundleRows
                        bundleResult={bundleResult}
                        columns={visibleColumns}
                        disabled={effectiveDisabled}
                        stickyFirstColumn={effectiveStickyFirstColumn}
                        frozenLeftCount={frozenLeftCount}
                        frozenLeftOffsetsByIndex={frozenLeftOffsetsByIndex}
                        protectedTypeIds={protectedTypeIds}
                        formulaTypeIds={formulaTypeIds}
                        formulaSubFieldsByColumn={formulaSubFieldsByColumn}
                        kitCalcDriverTypeIds={kitCalcDriverTypeIds}
                        manualReorderEnabled={dndEnabled}
                        cardMode={cardMode}
                      />
                    </ArTableBundleContext.Provider>
                  ) : (
                    flatRows
                  )}
                </SortableContext>
              </DndContext>
            )}
            <ArTableAggregations
              stickyFirstColumn={effectiveStickyFirstColumn}
              frozenLeftCount={frozenLeftCount}
              frozenLeftOffsetsByIndex={frozenLeftOffsetsByIndex}
              columns={visibleColumns}
              hidden={split !== null && !isPdfExportMode}
              pdfRowIds={isPdfExportMode ? filteredRowIds : undefined}
              cardMode={cardMode}
            />
          </div>
          {showInteractiveControls && (
            <ArTableControls
              tableId={tableId}
              parentPath={parentPath}
              disabled={effectiveDisabled}
              frozenLeftColumns={frozenLeftColumns}
              onFrozenLeftColumnsChange={setFrozenLeftColumns}
              columnConfig={columnConfig}
              cardMode={cardMode}
              footerOnly
            />
          )}
        </div>
      </FlexCol>
      <BulkEditConfirmationModal />
      <BundleMoveConfirmationModal
        open={pendingBundleMove !== null}
        targetBundleLabel={pendingBundleMove?.targetBundleLabel ?? null}
        onConfirm={confirmBundleMove}
        onCancel={() => setPendingBundleMove(null)}
      />
    </ArTableContextMenu>
  );
};

export default memo(ArTableBody);
